import { useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { CHANNEL_META, Row, useThemePortal, type RowData, type WizardChannel } from "./channel-wizard-kit";
import WhatsAppLogin from "./channel-wizard-whatsapp";
import WabaPanel from "./channel-wizard-waba";
import TelegramPanel from "./channel-wizard-telegram";
import TelegramLogin from "./channel-wizard-telegram-qr";
import ViberLogin from "./channel-wizard-viber";
import ViberBotPanel from "./channel-wizard-viber-bot";
import InstagramLogin from "./channel-wizard-instagram";
import InstagramFacebookPanel from "./channel-wizard-instagram-facebook";
import EmailLogin from "./channel-wizard-email";
import type { Channel as HubChannel } from "./inbox-api";

/* Мастер подключения каналов.
   WhatsApp — обычный аккаунт: QR-код или номер телефона с кодом.
   WABA — официальный WhatsApp Business API: Phone number ID и токен.
   Telegram — аккаунт по QR-коду (Подключить устройство).
   Telegram Bot — проверка токена бота от @BotFather.
   Viber — аккаунт по QR-коду с PIN-подтверждением.
   Viber Business — официальный аккаунт по токену.
   Instagram — вход по логину, паролю и коду 2FA.
   Instagram через Facebook — выбор бизнес-страницы в Meta. */

export type { WizardChannel } from "./channel-wizard-kit";

export default function ChannelWizard({
  channel,
  onClose,
  onConnected,
  existing,
}: {
  channel: WizardChannel;
  onClose: () => void;
  onConnected: (account: string) => void;
  /** переподключение уже созданного канала WhatsApp */
  existing?: HubChannel | null;
}) {
  const meta = CHANNEL_META[channel];
  const host = useThemePortal();
  const [result, setResult] = useState<{ account: string; rows: RowData[] } | null>(null);

  const done = Boolean(result);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4 md:p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-title"
        className="flash-modal relative flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-t-card border border-border sm:rounded-card"
      >
        {/* шапка мастера */}
        <div className="flex items-start gap-3 border-b border-border px-4 py-3.5 md:px-5">
          <span
            className="grid size-10 flex-none place-items-center rounded-btn bg-primary/10 text-xl leading-none text-primary"
            aria-hidden
          >
            <i className={meta.icon} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t("Подключение канала")}
            </p>
            <h3 id="wizard-title" className="mt-0.5 truncate text-[15px] font-semibold leading-snug md:text-base">
              {meta.name}
              <span className="ms-2 hidden text-[12px] font-normal text-muted-foreground sm:inline">
                {meta.sub}
              </span>
            </h3>
          </div>
          <span
            className={cn(
              "hidden flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold sm:inline-flex",
              done ? "bg-emerald-500/15 text-emerald-600" : "bg-primary/10 text-primary",
            )}
          >
            <span
              className={cn("size-1.5 rounded-full", done ? "bg-emerald-500" : "bg-primary")}
              aria-hidden
            />
            {done ? t("Подключено") : t("Мастер")}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть мастер подключения")}
            className="grid size-8 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        {result ? (
          <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 text-center md:px-6">
            <span
              className="grid size-16 place-items-center rounded-full bg-emerald-500 text-white"
              aria-hidden
            >
              <Check className="size-8" strokeWidth={3} />
            </span>
            <h4 className="mt-4 text-lg font-semibold">{t("{name} подключён", { name: meta.name })}</h4>
            <p className="mt-1.5 max-w-sm text-[12.5px] leading-relaxed text-muted-foreground">
              {meta.doneText}
            </p>
            <div className="mt-4 w-full max-w-sm rounded-card border border-border bg-background/60 p-3 text-start">
              {result.rows.map((r) => (
                <Row key={r.label} label={r.label} value={r.value} />
              ))}
            </div>
            <div className="mt-5 flex w-full max-w-sm flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
              >
                {t("Закрыть")}
              </button>
              <button
                type="button"
                onClick={() => {
                  onConnected(result.account);
                  onClose();
                }}
                className="inline-flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                {t("Готово")}
                <ArrowRight className="size-3.5" aria-hidden />
              </button>
            </div>
          </div>
        ) : (
          <>
            {channel === "whatsapp" ? <WhatsAppLogin existing={existing} onDone={(account, rows) => setResult({ account, rows })} /> : null}
            {channel === "waba" ? <WabaPanel onDone={(account, rows) => setResult({ account, rows })} /> : null}
            {channel === "telegram" ? <TelegramLogin existing={existing} onDone={(account, rows) => setResult({ account, rows })} /> : null}
            {channel === "telegram-bot" ? (
              <TelegramPanel onDone={(account, rows) => setResult({ account, rows })} />
            ) : null}
            {channel === "viber" ? (
              <ViberLogin onDone={(account, rows) => setResult({ account, rows })} />
            ) : null}
            {channel === "viber-bot" ? (
              <ViberBotPanel onDone={(account, rows) => setResult({ account, rows })} />
            ) : null}
            {channel === "instagram" ? (
              <InstagramLogin onDone={(account, rows) => setResult({ account, rows })} />
            ) : null}
            {channel === "instagram-facebook" ? (
              <InstagramFacebookPanel onDone={(account, rows) => setResult({ account, rows })} />
            ) : null}
            {channel === "email" ? <EmailLogin existing={existing} onDone={(account, rows) => setResult({ account, rows })} /> : null}
          </>
        )}

        {!done ? (
          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2.5 md:px-5">
            <span className="flex items-center gap-1.5 text-[10.5px] text-muted-foreground">
              <span className="size-1.5 flex-none rounded-full bg-primary" aria-hidden />
              {channel === "whatsapp"
                ? t("Подключение настоящее: номер станет связанным устройством Hub")
                : channel === "telegram"
                  ? t("Подключение настоящее: аккаунт станет связанным устройством Hub")
                  : channel === "email"
                    ? t("Подключение настоящее: письма ящика читаются через Google Workspace")
                    : t("Код и проверки настоящие, привязка — в демо-режиме")}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 flex-none cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-3 text-[11.5px] font-semibold text-muted-foreground transition hover:text-foreground"
            >
              {t("Позже")}
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    host,
  );
}